import type { AppConfig } from '../state/config.ts';
import { DEFAULT_CONFIG } from '../state/config.ts';
import { loadAccountSources, saveAccountSources } from '../state/sourceCache.ts';
import {
  scopedLsGet,
  scopedLsSet,
  loadScopedDraftOverrides,
  saveScopedDraftOverrides,
  type StoredDraftOverride,
} from '../state/storage.ts';
import type { AccountSnapshot, SleeperProfile, SyncedSetup } from './types.ts';

const PROFILES_KEY = 'profiles';
const SOURCE_VERSIONS_KEY = 'source_versions';

function setupFromConfig(config: AppConfig): SyncedSetup {
  const { sleeper_username: _username, sleeper_user_id: _userId, ...setup } = config;
  return setup;
}

export function profileFromConfig(config: AppConfig): SleeperProfile | null {
  if (!config.sleeper_username || !config.sleeper_user_id) return null;
  return {
    sleeper_user_id: config.sleeper_user_id,
    sleeper_username: config.sleeper_username,
    setup: setupFromConfig(config),
    updated_at: null,
    last_used_at: new Date().toISOString(),
  };
}

export function configFromProfile(profile: SleeperProfile, current: AppConfig = DEFAULT_CONFIG): AppConfig {
  return {
    ...current,
    ...profile.setup,
    sleeper_username: profile.sleeper_username,
    sleeper_user_id: profile.sleeper_user_id,
  };
}

export async function loadCachedAccountSnapshot(userId: string): Promise<AccountSnapshot> {
  const profiles = scopedLsGet<SleeperProfile[]>(userId, PROFILES_KEY, []);
  const sources = await loadAccountSources(userId);
  const source_versions = scopedLsGet<Record<string, string | null>>(userId, SOURCE_VERSIONS_KEY, {});
  return { profiles, sources, source_versions };
}

export async function cacheAccountSnapshot(userId: string, snapshot: AccountSnapshot): Promise<void> {
  scopedLsSet(userId, PROFILES_KEY, snapshot.profiles);
  scopedLsSet(userId, SOURCE_VERSIONS_KEY, snapshot.source_versions);
  await saveAccountSources(userId, snapshot.sources);
}

export function loadAccountDraftOverrides(userId: string, draftId: string): StoredDraftOverride[] {
  return loadScopedDraftOverrides(userId, draftId);
}

export function saveAccountDraftOverrides(userId: string, draftId: string, overrides: StoredDraftOverride[]): void {
  saveScopedDraftOverrides(userId, draftId, overrides);
}
